/**
 * "Start onboarding" (plan docs/plans/lms-plan.md 12.2, 18.24; WP-47; R1,
 * R14): the person (the directory, WP-43's UserPicker), the template (the
 * default pre-selected), the site (WP-43's SitePicker; the current site
 * pre-selected) and the start date (the hire's first day; due dates count
 * from it). POST /admin/onboarding/runs answers 201 { run, items, progress,
 * assignments } and the caller opens the run.
 *
 * The server's refusals shown as they come (18.24): the person or the site
 * out of reach (404), a person without access to the chosen site (400), an
 * inactive account (409), a template that is retired, has no items or links
 * an unpublished course (409), and a second active run (409 carrying
 * active_run_id, which this dialog offers to open). With no template in the
 * network the dialog says so and points to Manage, Onboarding templates (12.5).
 */
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Modal from '../../../../ui/Modal';
import FilterSelect from '../../../../ui/FilterSelect';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { useLms } from '../../../LmsProvider';
import { UserPicker } from '../../../components/pickers/UserPicker';
import { SitePicker } from '../../../components/pickers/SitePicker';
import { ErrorNote, labelCls, metaCls } from '../../../components/pickers/shared';
import { activeRunIdOf, listOnboardingTemplates, startOnboardingRun } from '../../../api/onboarding';
import type { OnboardingStartResult, OnboardingTemplateListItem } from '../../../api/onboarding';
import { describeOnboardingError, localToday, personLabel } from '../../onboarding/shared';
import { pickSingle, TEMPLATES_HREF } from './paths';

export interface StartRunDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** The run was started: the answer and the person's name. */
  onStarted: (result: OnboardingStartResult, personName: string) => void;
  /** Open an existing run (a second active run's 409). */
  onOpenRun: (runId: string) => void;
}

export function StartRunDialog({ isOpen, onClose, onStarted, onOpenRun }: StartRunDialogProps) {
  const { siteId: currentSiteId } = useLms();
  const [personId, setPersonId] = useState<string | null>(null);
  const [personName, setPersonName] = useState('');
  const [templateId, setTemplateId] = useState('');
  // Seeded from the current site and today at mount, so the pickers' first read is already the right one.
  const [siteId, setSiteId] = useState<string | null>(currentSiteId);
  const [startDate, setStartDate] = useState(localToday);
  const [templates, setTemplates] = useState<OnboardingTemplateListItem[] | null>(null);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPersonId(null);
    setPersonName('');
    setTemplateId('');
    setSiteId(currentSiteId);
    setStartDate(localToday());
    setSaving(false);
    setError(null);
    setActiveRunId(null);
  }, [isOpen, currentSiteId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setTemplates(null);
    setTemplatesError(null);
    listOnboardingTemplates({ limit: 200 })
      .then((page) => {
        if (cancelled) return;
        const active = (Array.isArray(page.items) ? page.items : []).filter((t) => t.is_active !== false);
        setTemplates(active);
        // 12.2: the default template is pre-selected (else the only one there is).
        const preset = active.find((t) => t.is_default) ?? (active.length === 1 ? active[0] : null);
        setTemplateId(preset ? preset.id : '');
      })
      .catch((err) => {
        if (!cancelled) setTemplatesError(describeOnboardingError(err, 'The onboarding templates could not be loaded.'));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, attempt]);

  const template = useMemo(() => (templates ?? []).find((t) => t.id === templateId) ?? null, [templates, templateId]);
  const noTemplate = templates !== null && templates.length === 0;
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(startDate);
  const canStart = Boolean(personId && template && siteId && dateOk) && !saving;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canStart || !personId || !template || !siteId) return;
    setSaving(true);
    setError(null);
    setActiveRunId(null);
    try {
      const result = await startOnboardingRun({ user_id: personId, template_id: template.id, site_id: siteId, start_date: startDate });
      onStarted(result, personName || 'the new team member');
    } catch (err) {
      setActiveRunId(activeRunIdOf(err));
      setError(describeOnboardingError(err, 'Onboarding could not be started.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose} title="Start onboarding" maxWidth="max-w-2xl">
      <form onSubmit={submit} className="space-y-4" data-testid="onboarding-start-form">
        {noTemplate ? (
          <div
            className="rounded-xl border border-dashed border-gray-300 p-6 text-center dark:border-gray-600"
            data-testid="onboarding-start-no-template"
          >
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No onboarding template exists yet</p>
            <p className={`${metaCls} mt-1`}>A run copies its items from a template. Create one first.</p>
            <Link to={TEMPLATES_HREF} className={`${secondaryBtnCls} mt-3`} onClick={onClose}>
              Open Onboarding templates
            </Link>
          </div>
        ) : null}

        <SitePicker
          value={siteId ? [siteId] : []}
          onChange={(ids) => setSiteId(pickSingle(ids, siteId))}
          label="Site"
          disabled={saving}
        />

        <UserPicker
          value={personId ? [personId] : []}
          onChange={(ids, people) => {
            const next = pickSingle(ids, personId);
            setPersonId(next);
            const person = people.find((p) => p.user_id === next);
            setPersonName(person ? personLabel(person) : next ? personName : '');
          }}
          siteId={siteId}
          label="New team member"
          disabled={saving}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            {templatesError ? (
              <ErrorNote>
                {templatesError}{' '}
                <button type="button" className="font-medium underline" onClick={() => setAttempt((n) => n + 1)}>
                  Retry
                </button>
              </ErrorNote>
            ) : templates === null ? (
              <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
                Loading templates
              </p>
            ) : (
              <div data-testid="onboarding-start-template">
                <div className={labelCls} aria-hidden="true">
                  Template
                </div>
                <FilterSelect value={templateId} onChange={setTemplateId} label="Template" disabled={saving || noTemplate}>
                  <option value="">Choose a template</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {`${t.name}${t.is_default ? ' (default)' : ''}, ${t.item_count} ${t.item_count === 1 ? 'item' : 'items'}`}
                    </option>
                  ))}
                </FilterSelect>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="lms-onboarding-start-date" className={labelCls}>
              First day
            </label>
            <input
              id="lms-onboarding-start-date"
              type="date"
              className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={saving}
              data-testid="onboarding-start-date"
            />
            <p className={`${metaCls} mt-1`}>Due dates count from this day.</p>
          </div>
        </div>

        {template && template.item_count === 0 ? (
          <p className="text-sm text-amber-700 dark:text-amber-400">This template has no items yet; add them under Onboarding templates first.</p>
        ) : null}

        {error ? (
          <ErrorNote testId="onboarding-start-error">
            <p>{error}</p>
            {activeRunId ? (
              <button
                type="button"
                className={`${secondaryBtnCls} mt-2`}
                onClick={() => onOpenRun(activeRunId)}
                data-testid="onboarding-open-active-run"
              >
                Open the active run
              </button>
            ) : null}
          </ErrorNote>
        ) : null}

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnCls} disabled={!canStart} data-testid="onboarding-start-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Start onboarding
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default StartRunDialog;
